/**
 * ProfilePhotoCapture.jsx — Live camera capture for member/vendor
 * profile photos.
 *
 * Owner-mandated: file uploads are NOT accepted (they could be a
 * photoshopped image). Every photo comes straight from the device
 * camera via `getUserMedia`. Works on both phones (front camera)
 * AND desktop webcams — same API, same UX.
 *
 * Props:
 *   • currentUrl   — string, current avatar (shown when idle)
 *   • onSave       — async ({ dataUrl, blob }) → any (backend persist)
 *   • label        — string, alt text under the ring
 */
import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Loader2, Check, X, CameraOff } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePhotoCapture({ currentUrl, onSave, label }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("idle"); // idle | starting | streaming | snapped | saving
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => () => stopCamera(), []);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  async function startCamera() {
    setError(null);
    setMode("starting");
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera API not available in this browser.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 720, height: 720 },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setMode("streaming");
    } catch (e) {
      setError(e?.message || "Camera unavailable. Please allow camera access or use a phone.");
      setMode("idle");
    }
  }

  function snap() {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const short = Math.min(v.videoWidth || 720, v.videoHeight || 720);
    c.width = short; c.height = short;
    const ctx = c.getContext("2d");
    const sx = ((v.videoWidth || 720)  - short) / 2;
    const sy = ((v.videoHeight || 720) - short) / 2;
    ctx.drawImage(v, sx, sy, short, short, 0, 0, short, short);
    c.toBlob((blob) => {
      setPreview({ blob, url: URL.createObjectURL(blob), dataUrl: c.toDataURL("image/jpeg", 0.85) });
      setMode("snapped");
      stopCamera();
    }, "image/jpeg", 0.85);
  }

  function retake() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    startCamera();
  }

  async function save() {
    if (!preview) return;
    setMode("saving");
    try {
      await onSave?.({ dataUrl: preview.dataUrl, blob: preview.blob });
      toast.success("Photo updated.");
      if (preview.url) URL.revokeObjectURL(preview.url);
      setPreview(null);
      setMode("idle");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed.");
      setMode("snapped");
    }
  }

  const showRing = mode === "idle" && !preview;
  const showLive = mode === "streaming" || mode === "starting";
  const showPreview = mode === "snapped" || mode === "saving";

  return (
    <div
      data-testid="profile-photo-capture"
      className="flex items-start gap-5"
    >
      <div className="relative shrink-0">
        {showRing && (
          <div
            className="w-24 h-24 md:w-28 md:h-28 rounded-full ring-4 ring-[#E8E5DF] bg-[#F3EFE7] overflow-hidden flex items-center justify-center text-3xl text-[#5A7247]"
            data-testid="profile-photo-ring"
          >
            {currentUrl ? (
              <img
                src={currentUrl}
                alt={label || "Profile photo"}
                className="w-full h-full object-cover"
              />
            ) : (
              (label || "?").trim().charAt(0).toUpperCase()
            )}
          </div>
        )}
        {(showLive || showPreview) && (
          <div className="w-40 h-40 rounded-2xl ring-4 ring-[#E8E5DF] overflow-hidden bg-black relative">
            <video
              ref={videoRef}
              playsInline
              muted
              className={`w-full h-full object-cover ${showPreview ? "hidden" : ""}`}
              data-testid="profile-photo-video"
            />
            {showPreview && preview && (
              <img
                src={preview.url}
                alt="New photo preview"
                className="w-full h-full object-cover"
                data-testid="profile-photo-preview"
              />
            )}
            {mode === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
                <Loader2 className="h-4 w-4 animate-spin mr-1" /> Starting camera…
              </div>
            )}
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#3C423F]">
          Profile photo
        </p>
        <p className="mt-1 text-sm text-[#3C423F] leading-relaxed">
          For trust and safety we only accept live camera captures —
          phone or laptop webcam. File uploads aren't allowed
          (they can be photoshopped).
        </p>

        {mode === "idle" && !preview && (
          <button
            type="button"
            onClick={startCamera}
            data-testid="profile-photo-start"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#5A7247] hover:bg-[#496538] text-white px-5 h-10 text-sm font-medium"
          >
            <Camera className="h-4 w-4" />
            {currentUrl ? "Change photo" : "Take photo"}
          </button>
        )}

        {mode === "streaming" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={snap}
              data-testid="profile-photo-snap"
              className="inline-flex items-center gap-2 rounded-full bg-[#C05746] hover:bg-[#A64A3C] text-white px-5 h-10 text-sm font-medium"
            >
              <Camera className="h-4 w-4" /> Snap
            </button>
            <button
              type="button"
              onClick={() => { stopCamera(); setMode("idle"); }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E5DF] text-[#3C423F] px-4 h-10 text-sm"
            >
              <CameraOff className="h-4 w-4" /> Cancel
            </button>
          </div>
        )}

        {mode === "snapped" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={save}
              data-testid="profile-photo-save"
              className="inline-flex items-center gap-2 rounded-full bg-[#5A7247] hover:bg-[#496538] text-white px-5 h-10 text-sm font-medium"
            >
              <Check className="h-4 w-4" /> Use this photo
            </button>
            <button
              type="button"
              onClick={retake}
              data-testid="profile-photo-retake"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E5DF] text-[#3C423F] px-4 h-10 text-sm"
            >
              <RefreshCw className="h-4 w-4" /> Retake
            </button>
          </div>
        )}

        {mode === "saving" && (
          <div className="mt-3 text-sm text-[#3C423F]">
            <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Saving…
          </div>
        )}

        {error && (
          <p
            data-testid="profile-photo-error"
            className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2"
          >
            <X className="inline h-4 w-4 mr-1" /> {error}
          </p>
        )}
      </div>
    </div>
  );
}
